import React, {useState, useCallback} from 'react';
import {Iterable} from 'immutable';
import classnames from 'classnames';
import {Badge, Icon, IconButton, Menu, RangeInput, Text, WithTooltip} from '@albert-io/atomic';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import './passage.scss';
import {
  NoTranslationWarningHeader,
  notAvailableText,
  showPassage,
  hidePassage
} from 'client/components/QuestionTypes/shared/TranslationSection';
import FeatureFlag from 'client/components/FeatureFlag/FeatureFlag.react';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import {FONT_SIZES} from '@albert-io/atomic/atoms/Text/Text.react';
import classNames from 'classnames';
import CustomScrollbar from './CustomScrollbar';
import {noop} from 'lodash';

interface Props {
  content: any;
  noTranslation?: boolean;
  showTranslationInfo?: boolean;
  showTranslatedSupplements?: boolean;
  showBorders?: boolean;
}

interface MakeLineProps {
  hideLineNumbers: boolean;
  excludeWhiteSpace: boolean;
  lineNumberAlignment?: string;
  excludeHeaders?: boolean;
  lineNumberIncrement?: number;
}

// makes it easier for the range input, since FONT_SIZES is ordered largest to smallest
const reversedFontSizes = FONT_SIZES.slice().reverse();
const FONT_SIZE_IDX_REGULAR = reversedFontSizes.indexOf('regular');

const Passage = ({
  content,
  noTranslation = false,
  showTranslatedSupplements = false,
  showTranslationInfo = false,
  showBorders = true
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [fontSizeIndex, setFontSizeIndex] = useState(FONT_SIZE_IDX_REGULAR);
  const [showLines, setShowLines] = useState(true);

  const {isFeatureEnabled: isPassageSupplementsUpdatesEnabled} = useIsFeatureFlagEnabled(
    'passage_supplements_updates'
  );

  const toggleIsExpanded = useCallback(() => {
    setIsExpanded((prevIsExpanded) => !prevIsExpanded);
  }, []);

  const getContent = useCallback(() => {
    return Iterable.isIterable(content) ? content.toJS() : content;
  }, [content]);

  const hasContent = useCallback((line) => {
    return /[^\s]/.test(line);
  }, []);

  const getFormattedLines = useCallback(
    (text: string, ignoreWhiteSpace: boolean) => {
      if (text) {
        const splitText = text.split(/[\n\r]/);
        return ignoreWhiteSpace ? splitText.filter(hasContent) : splitText;
      }
      return null;
    },
    [hasContent]
  );

  const makeLines = useCallback(
    (lines: string[] | null, lineProps: MakeLineProps) => {
      const {
        hideLineNumbers,
        excludeWhiteSpace,
        lineNumberAlignment,
        excludeHeaders,
        lineNumberIncrement
      } = lineProps;

      let wasPrevLineEmpty = false;
      let lineNumber = 0;
      if (lines) {
        return lines.map((line, i) => {
          const isEmptyLine = !hasContent(line);
          const isHeaderLine = line.charAt(0) === '#';
          if (wasPrevLineEmpty && isEmptyLine) {
            return null;
          }
          wasPrevLineEmpty = isEmptyLine;
          if (!excludeWhiteSpace || !isEmptyLine) {
            if (!excludeHeaders || !isHeaderLine) {
              lineNumber += 1;
            }
          }

          return (
            <PassageLine
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              hideLineNumbers={hideLineNumbers}
              lineNumber={
                excludeWhiteSpace && isEmptyLine
                  ? null
                  : isHeaderLine && excludeHeaders
                    ? null
                    : lineNumber
              }
              line={isEmptyLine ? '\u00a0' : line}
              isPassageSupplementsUpdatesEnabled={isPassageSupplementsUpdatesEnabled}
              fontSize={reversedFontSizes[fontSizeIndex]}
              lineNumberAlignment={lineNumberAlignment}
              lineNumberIncrement={lineNumberIncrement}
            />
          );
        });
      }
      return null;
    },
    [hasContent, isPassageSupplementsUpdatesEnabled, fontSizeIndex]
  );

  const {
    excludeWhiteSpace,
    hideLineNumbers,
    ignoreWhiteSpace,
    text,
    lineNumberAlignment,
    lineNumberIncrement,
    excludeHeaders,
    setMaxHeight,
    title
  } = getContent();
  const lines = getFormattedLines(text, ignoreWhiteSpace);

  const showWarning = (noTranslation || !showTranslatedSupplements) && showTranslationInfo;

  const toggleText = `${isExpanded ? 'Hide' : 'Show'} Passage`;
  const translatedToggleText = isExpanded ? hidePassage : showPassage;

  const lineProps = {
    hideLineNumbers,
    excludeWhiteSpace,
    lineNumberAlignment,
    excludeHeaders,
    lineNumberIncrement
  };

  const passageContent = (
    <FeatureFlag name='passage_supplements_updates'>
      <div
        className={classNames('flex items-center justify-between border-border-primary', {
          'border-b': showLines
        })}
      >
        <div className='min-w-0 flex-1 truncate px-x2 py-x1-half font-bold'>
          {title || (showLines ? <>&nbsp;</> : 'Passage')}
        </div>
        <div className='flex items-center justify-between pr-x2'>
          {showLines && (
            <Menu position='bottom-end'>
              <Menu.Trigger>
                {(triggerProps) => (
                  <WithTooltip content='Text size'>
                    <IconButton
                      {...triggerProps}
                      color='secondary'
                      variant='text'
                      size='s'
                      icon='text-size'
                      label=''
                      iconStyle='regular'
                    />{' '}
                  </WithTooltip>
                )}
              </Menu.Trigger>
              <Menu.Content>
                <Menu.Item
                  disableHover
                  disableActive
                  className='flex items-center justify-center p-x1'
                >
                  <IconButton
                    icon='font'
                    iconStyle='light'
                    label=''
                    size='xs'
                    disabled={fontSizeIndex === 0}
                    onClick={() => setFontSizeIndex(fontSizeIndex - 1)}
                  />
                  <RangeInput
                    label='Range Input'
                    min={0}
                    max={FONT_SIZES.length - 1}
                    value={fontSizeIndex}
                    onChange={(e) => setFontSizeIndex(Number(e.target.value))}
                  />
                  <IconButton
                    icon='font'
                    iconStyle='light'
                    size='xs'
                    label=''
                    className='text-[16px]!'
                    disabled={fontSizeIndex === FONT_SIZES.length - 1}
                    onClick={() => setFontSizeIndex(fontSizeIndex + 1)}
                  />
                </Menu.Item>
                <Menu.Item disableHover disableActive className='flex justify-center p-x1'>
                  <Text
                    className='cursor-pointer'
                    onClick={() =>
                      fontSizeIndex !== FONT_SIZE_IDX_REGULAR
                        ? setFontSizeIndex(FONT_SIZE_IDX_REGULAR)
                        : noop
                    }
                    size='s'
                    disabled={fontSizeIndex === FONT_SIZE_IDX_REGULAR}
                  >
                    Reset size
                  </Text>
                </Menu.Item>
              </Menu.Content>
            </Menu>
          )}
          {showBorders && (
            <WithTooltip content={showLines ? 'Hide' : 'Show'}>
              <IconButton
                color='secondary'
                variant='text'
                size='s'
                icon='arrows-to-line'
                label=''
                iconStyle='regular'
                onClick={() => setShowLines(!showLines)}
              />
            </WithTooltip>
          )}
        </div>
      </div>
      {showLines && (
        <>
          {setMaxHeight ? (
            <CustomScrollbar className='passage-supplement__scrollable max-h-[304px]'>
              <ul className={'m-x0 max-h-[304px] min-h-full p-x2 pt-x1-half'}>
                {makeLines(lines, lineProps)}
              </ul>
            </CustomScrollbar>
          ) : (
            <ul className={'m-x0 h-full p-x2 pt-x1-half'}>{makeLines(lines, lineProps)}</ul>
          )}
        </>
      )}
      <FeatureFlag.Fallback>
        <div className='passage-supplement'>
          <button
            type='button'
            onClick={toggleIsExpanded}
            className={classnames('passage-supplement__toggle', {
              'passage-supplement__toggle--expanded': isExpanded
            })}
          >
            <Icon
              icon={isExpanded ? 'caret-down' : 'caret-right'}
              className='passage-supplement__icon'
            />
            {showTranslationInfo ? translatedToggleText : toggleText}
          </button>
          {isExpanded && (
            <ul className='passage-supplement__passage'>{makeLines(lines, lineProps)}</ul>
          )}
        </div>
      </FeatureFlag.Fallback>
    </FeatureFlag>
  );

  return showWarning ? (
    <NoTranslationWarningHeader text={notAvailableText} supplement>
      {passageContent}
    </NoTranslationWarningHeader>
  ) : (
    <div
      className={classNames('rounded-2xl border-border-primary', {
        'mx-auto border': showBorders && isPassageSupplementsUpdatesEnabled,
        'border-b': !showBorders && setMaxHeight && isPassageSupplementsUpdatesEnabled && showLines
      })}
    >
      {passageContent}
    </div>
  );
};

export default Passage;

interface PassageLineProps {
  hideLineNumbers: boolean;
  lineNumber: number | null;
  line: string;
  isPassageSupplementsUpdatesEnabled?: boolean;
  fontSize?: (typeof FONT_SIZES)[number];
  lineNumberAlignment?: string;
  lineNumberIncrement?: number;
}

const PassageLine = ({
  hideLineNumbers,
  lineNumber,
  line,
  isPassageSupplementsUpdatesEnabled,
  fontSize,
  lineNumberAlignment,
  lineNumberIncrement
}: PassageLineProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const shouldShowLineNumber =
    !hideLineNumbers &&
    lineNumber !== null &&
    (lineNumberIncrement == null || lineNumber % lineNumberIncrement === 0 || isHovered);

  const shouldReserveSpace = !hideLineNumbers;

  return isPassageSupplementsUpdatesEnabled ? (
    <li
      className={classnames('passage-supplement-line--new', {
        'passage-line__badge-left':
          shouldReserveSpace && (lineNumberAlignment === 'left' || !lineNumberAlignment),
        'passage-line__badge-right': shouldReserveSpace && lineNumberAlignment === 'right'
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {shouldReserveSpace && (lineNumberAlignment === 'left' || !lineNumberAlignment) && (
        <Badge
          className={classNames('passage-supplement-line__number--new mr-x1', {
            invisible: !shouldShowLineNumber
          })}
          status='neutral'
          size='small'
        >
          {lineNumber}
        </Badge>
      )}
      <Text size={fontSize} className='w-full'>
        <MarkdownRendererV2 text={line} />
      </Text>
      {shouldReserveSpace && lineNumberAlignment === 'right' && (
        <Badge
          className={classNames('passage-supplement-line__number--new ml-x1', {
            invisible: !shouldShowLineNumber
          })}
          status='neutral'
          size='small'
        >
          {lineNumber}
        </Badge>
      )}
    </li>
  ) : (
    <li className='passage-supplement-line'>
      {shouldShowLineNumber && (
        <Text bold size='m' color='secondary' className='passage-supplement-line__number'>
          {lineNumber}
        </Text>
      )}
      <MarkdownRendererV2 text={line} />
    </li>
  );
};
